import 'babel-polyfill'; // provides ES6 (and beyond) shim support for IE11
import '@/support/config';
import '@/api';
import PACONST from '@/support/constants';
import Vue from 'vue';
import store from '@/store';
import router from '@/router';
import i18n from '@/i18n';
import '@/components/global';
import 'roboto-fontface/css/roboto/roboto-fontface.css';
import App from '@/components/layout/App';
import { format } from '@/support/ui';
import { ajaxError, validationError } from '@/support/error';

import axios from 'axios';
import moment from 'moment';
import 'moment-timezone';

import Vuetify from 'vuetify/lib';
import colors from '@/styles/variables.scss';
import '@/styles/styles.scss';

import VueScrollTo from 'vue-scrollto';

import Cove from '@ctrack/cove';

/**
 * Simple dummy Cove hook for PA this simply clears out anything that can't be interpreted as a date in a date field. If we ever
 * need to support large data entry forms or validation in PA this will have to be revisted.
 *
 * @param {String} str The user's input into the date field
 */
function processDatePickerShortcut (str) {
  return Promise.resolve(null);
}

Vue.use(Cove, { store, i18n, processDatePickerShortcut });

Vue.use(Vuetify);

const vuetify = new Vuetify({
  theme: {
    themes: {
      light: {
        primary: colors.primary,
        secondary: colors.secondary,
        accent: colors.accent,
        success: colors.success,
        error: colors.error,
        info: colors.info,
        warning: colors.warning
      }
    }
  },
  options: {
    themeVariations: ['primary', 'secondary', 'accent', 'success', 'warning', 'error', 'info']
  },
  icons: {
    iconfont: 'mdi'
  }
});

Vue.use(VueScrollTo);

moment.locale(Cove.getConfig('locale'));
moment.tz.setDefault(Cove.getConfig('timezone'));
Vue.config.productionTip = false;

// store for promises/actions that need to be resolved before starting the application
let promises = [];

// setup global spinner/blocking on ajax requests
let pendingAjaxCount = 0;
axios.interceptors.request.use((config) => {
  Cove.block();
  pendingAjaxCount++;
  return config;
});

axios.interceptors.response.use(
  (response) => {
    pendingAjaxCount--;
    if (pendingAjaxCount === 0)
    {
      Cove.unblock();
    }

    return response;
  },
  (error) => {
    pendingAjaxCount--;
    if (pendingAjaxCount === 0)
    {
      Cove.unblock();
    }

    let statusCode = error.response.status;
    if (statusCode === 400)
    {
      let response = {};
      try
      {
        response = JSON.parse(error.response.data);
      }
      catch (err) {
        // eat error
      }

      if (response.validationErrors)
      {
        validationError(response.validationErrors);
        return Promise.reject(error);
      }
    }

    ajaxError();
    return Promise.reject(error);
  }
);

// opt out of self links
function excludeSelfLinksRequestInterceptor (config)
{
  config.headers['X-CTrack-ExcludeSelfLinks'] = true;

  return config;
}

axios.interceptors.request.use(excludeSelfLinksRequestInterceptor);

Vue.use({
  install (Vue) {
    Vue.mixin({
      filters: {
        format (value, formatType) {
          return format(value, formatType);
        }
      },
      methods: {
        _setPageTitle () {
          if (this._pageTitle)
          {
            document.title = this._pageTitle + ' | ' + this.$t('app.title');
          }
        }
      },
      watch: {
        _pageTitle: {
          immediate: true,
          handler () {
            this._setPageTitle();
          }
        }
      }
    });

    Vue.prototype.$PACONST = PACONST;
    Vue.prototype.$axios = axios;
  }
});

// check to see if this user is already logged in, and if so, recover their permissions and username.
// this occurs when a user is logged in and reloads their tab or opens a link in a new tab.
if (store.getters['user/isLoggedIn'])
{
  promises.push(store.dispatch('user/recover'));
}

// after all promises are resolved, start the application
Promise.all(promises).then(() => {
  // eslint-disable-next-line no-new
  new Vue({
    el: '#app',
    router,
    store,
    i18n,
    vuetify,
    render: (h) => {return h(App);}
  });
});
